import { useCallback } from 'react';
import { usePermissionsContext } from '../context/PermissionContext';

/**
 * هذا الهوك مسؤول عن إدارة الصلاحيات المتعلقة بصفحات الفواتير الديناميكية
 * يستخدم سياق الصلاحيات العامة ويوفر طرقًا مخصصة للتحقق من صلاحيات الفواتير
 */
export const useInvoicePermissions = () => {
  const { hasPermission } = usePermissionsContext();

  /**
   * التحقق من صلاحية صفحة فاتورة معينة بناءً على المعرف التسلسلي للصفحة
   * @param invoicePageId معرف صفحة الفاتورة التسلسلي
   * @param action الإجراء المطلوب التحقق منه
   * @returns {boolean} صحيح إذا كان المستخدم يملك الصلاحية، خاطئ إذا لم يملكها
   */
  const hasInvoicePagePermission = useCallback(
    (invoicePageId: string | number, action: string): boolean => {
      // تكوين معرف الصفحة بناءً على المعرف
      let pageIdentifier: string;
      
      if (typeof invoicePageId === 'string' && invoicePageId.length > 10) {
        // إذا كان المعرف هو معرف MongoDB (_id)، نحاول العثور على الرقم التسلسلي
        // يتم تحميل معلومات الصفحة من ملف المكون أو الخدمة
        // كحل مؤقت، نستخدم الـ _id كاملاً
        pageIdentifier = `page-${invoicePageId}`;
        
        // محاولة استخدام parseInt مع التحقق - قد لا تكون الطريقة المثلى ولكنها حل مؤقت
        // نستخدم الأرقام الأولى في معرف MongoDB
        const numericPart = parseInt(String(invoicePageId), 10);
        if (!isNaN(numericPart)) {
          const alternativeId = `page-${numericPart}`;
          pageIdentifier = alternativeId;
        }
      } else {
        // إذا كان رقم تسلسلي عادي
        pageIdentifier = `page-${invoicePageId}`;
      }
      
      // إذا كانت الإجراءات الديناميكية (مخصصة لصفحات الفواتير)
      // نحتفظ بنفس قائمة الإجراءات المستخدمة في الخادم الخلفي (انظر إلى invoicePermissionService.js)
      const actionMappings: { [key: string]: string } = {
        'view_page': 'view_page',   // صلاحية الوصول للصفحة
        'create': 'create',         // إضافة فاتورة
        'view': 'view',             // مشاهدة فاتورة
        'edit': 'edit',             // تعديل فاتورة
        'delete': 'delete',         // حذف فاتورة
        'print': 'print',           // طباعة فاتورة
        'import_excel': 'import_excel', // استيراد اكسل
        'import_all_excel': 'import_all_excel', // استيراد كل الفواتير بالاكسل
        'import_pdf': 'import_pdf',     // استيراد pdf
        'view_payment_management': 'view_payment_management' // صلاحية نافذة تسديد الفواتير
      };
      
      // تحويل الإجراء إلى الاسم المناسب في النظام إذا كان مختلفًا
      const mappedAction = actionMappings[action] || action;
      
      // استخدام دالة التحقق من الصلاحيات العامة
      const hasPermissionResult = hasPermission('invoices', pageIdentifier, mappedAction);
      
      return hasPermissionResult;
    },
    [hasPermission]
  );

  /**
   * التحقق من صلاحية لصفحات الفواتير الثابتة
   * @param pageId معرف الصفحة (settings, print-templates, product-barcode)
   * @param action الإجراء المطلوب التحقق منه
   * @returns {boolean} صحيح إذا كان المستخدم يملك الصلاحية، خاطئ إذا لم يملكها
   */
  const hasInvoiceSettingPermission = useCallback(
    (pageId: string, action: string): boolean => {
      const result = hasPermission('invoices', pageId, action);
      
      return result;
    },
    [hasPermission]
  );

  return {
    hasInvoicePagePermission,
    hasInvoiceSettingPermission
  };
};